import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import {
  ARTIFACT_TYPE_LABEL,
  ARTIFACT_TYPE_VISUAL,
  generateArtifacts,
  triggerArtifactDownload,
  truncateError,
} from '@/api/artifacts';
import type {
  ArtifactType,
  GenerateBody,
  GenerateResponse,
} from '@/api/artifacts';
import type { DeclarationCase } from '@/api/cases';

/**
 * 生成申报材料弹窗（v4 M3-ρ-4）。
 *
 * 流程：
 *   1. 用户勾选 word/excel/pdf（默认全选） + 可选 template_pack_id
 *   2. 提交后调 POST /declaration-cases/{id}/artifacts
 *   3. 后端返 {succeeded, failed} 部分成功结果
 *   4. 弹窗内行内展示：成功条带「下载」按钮、失败条带红色错误（截断防栈泄漏）
 *   5. 用户主动「关闭」前不消失（不自动跳走，让用户先下载）
 *
 * 权限：UI 层不再做角色守卫（按钮入口已过滤）；真守卫在后端 require_permission('artifact:generate')。
 * 状态守卫：调用方（CaseActionsMenu）已按 canGenerateArtifacts(status) 过滤；
 *           即使绕过后端会返 409，错误会冒泡到 message.error。
 *
 * 不变量：
 *   - 0 hex 硬编码
 *   - 失败条带 truncateError() 防栈泄漏（ρ-3 SR-med#2）
 *   - 下载按钮走 triggerArtifactDownload()（authorized blob 路径，非 window.open）
 */
export interface GenerateArtifactsModalProps {
  open: boolean;
  case_: DeclarationCase | null;
  onCancel: () => void;
  /** 父组件可在 onDone 内 invalidate 列表 query */
  onDone?: () => void;
}

const ALL_TYPES: ArtifactType[] = ['word', 'excel', 'pdf'];

export function GenerateArtifactsModal({
  open,
  case_,
  onCancel,
  onDone,
}: GenerateArtifactsModalProps) {
  const [types, setTypes] = useState<ArtifactType[]>(ALL_TYPES);
  const [packId, setPackId] = useState<string>('');
  const [result, setResult] = useState<GenerateResponse | null>(null);

  // 打开时重置；关闭不动（防闪烁，与 RecordReviewModal 范式一致）
  useEffect(() => {
    if (open) {
      setTypes(ALL_TYPES);
      setPackId('');
      setResult(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: GenerateBody) => {
      if (!case_) {
        return Promise.reject(new Error('未选定申报案'));
      }
      return generateArtifacts(case_.id, body);
    },
    onSuccess: (res) => {
      setResult(res);
      if (res.succeeded.length > 0 && res.failed.length === 0) {
        message.success(`成功生成 ${res.succeeded.length} 个产物`);
      } else if (res.succeeded.length > 0 && res.failed.length > 0) {
        message.warning(
          `部分成功：${res.succeeded.length} 成功 / ${res.failed.length} 失败`,
        );
      } else {
        message.error(`全部失败（${res.failed.length} 个）`);
      }
      onDone?.();
    },
    onError: (err) => {
      // 生成接口未走 _silent（业务上失败信息常需展示），仍走 truncateError 防栈泄漏
      message.error(truncateError((err as Error)?.message ?? '生成失败'));
    },
  });

  const downloadMut = useMutation({
    mutationFn: (artifactId: string) => triggerArtifactDownload(artifactId),
    // triggerArtifactDownload 底层走 _silent —— 这里独占弹错，走 truncateError
    onError: (err) =>
      message.error(truncateError((err as Error)?.message ?? '下载失败')),
  });

  const canSubmit = types.length > 0 && !!case_ && !mutation.isPending;

  const subjectShort = useMemo(() => {
    if (!case_) return '—';
    return `${case_.subject_id.slice(0, 8)}…`;
  }, [case_]);

  return (
    <Modal
      title="生成申报材料"
      open={open}
      onCancel={() => {
        if (mutation.isPending) return; // 生成中不允许关闭
        onCancel();
      }}
      footer={
        result ? (
          <Space>
            <Button onClick={onCancel}>关闭</Button>
            <Button
              type="primary"
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  artifact_types: types,
                  template_pack_id: packId.trim() || undefined,
                })
              }
              disabled={!canSubmit}
            >
              再次生成
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={onCancel} disabled={mutation.isPending}>
              取消
            </Button>
            <Button
              type="primary"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() =>
                mutation.mutate({
                  artifact_types: types,
                  template_pack_id: packId.trim() || undefined,
                })
              }
            >
              开始生成
            </Button>
          </Space>
        )
      }
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              申报案：
              <code style={codeStyle}>{subjectShort}</code>
              <span style={{ marginLeft: 8, color: 'var(--color-text-tertiary)' }}>
                · 政策包：{case_?.frozen_policy_pack_version ?? '未冻结'}
              </span>
            </span>
          }
        />

        <Form layout="vertical" disabled={mutation.isPending}>
          <Form.Item
            label="选择产物类型"
            required
            help="默认全选；至少选一个"
          >
            <Checkbox.Group
              value={types}
              onChange={(v) => setTypes(v as ArtifactType[])}
            >
              <Space size={12}>
                {ALL_TYPES.map((t) => {
                  const visual = ARTIFACT_TYPE_VISUAL[t];
                  return (
                    <Checkbox key={t} value={t}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 'var(--radius-full)',
                            background: visual.fg,
                          }}
                        />
                        {ARTIFACT_TYPE_LABEL[t]}
                      </span>
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label="模板包 ID（可选）"
            help="留空走政策包默认模板（_default）；填 UUID 或目录名"
          >
            <Input
              value={packId}
              onChange={(e) => setPackId(e.target.value)}
              placeholder="如 sfl_standard_2025_v1 或留空"
              maxLength={128}
              allowClear
            />
          </Form.Item>
        </Form>

        {result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Typography.Text
              strong
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
              }}
            >
              生成结果
              <Tag color="green" style={{ marginLeft: 8 }}>
                成功 {result.succeeded.length}
              </Tag>
              {result.failed.length > 0 && (
                <Tag color="red">失败 {result.failed.length}</Tag>
              )}
            </Typography.Text>

            {result.succeeded.map((art) => {
              const visual = ARTIFACT_TYPE_VISUAL[art.artifact_type];
              return (
                <div key={art.id} style={resultRowStyle(visual.border)}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        background: visual.bg,
                        color: visual.fg,
                        border: `1px solid ${visual.border}`,
                        borderRadius: 'var(--radius-pill)',
                        padding: '0 8px',
                        fontSize: 'var(--font-size-xs)',
                        lineHeight: '18px',
                      }}
                    >
                      {ARTIFACT_TYPE_LABEL[art.artifact_type]}
                    </span>
                    <Typography.Text
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      hash {art.file_hash.slice(0, 10)}…
                    </Typography.Text>
                  </span>
                  <Button
                    size="small"
                    type="link"
                    loading={
                      downloadMut.isPending && downloadMut.variables === art.id
                    }
                    onClick={() => downloadMut.mutate(art.id)}
                  >
                    下载
                  </Button>
                </div>
              );
            })}

            {result.failed.map((f, idx) => (
              <div
                key={`fail-${idx}-${f.artifact_type}`}
                style={resultRowStyle('var(--color-danger-3)', true)}
              >
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Tag color="red" style={{ margin: 0 }}>
                      {f.artifact_type}
                    </Tag>
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-danger-2)',
                      }}
                    >
                      失败
                    </span>
                  </span>
                  <Typography.Text
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      wordBreak: 'break-word',
                    }}
                  >
                    {truncateError(f.error_message)}
                  </Typography.Text>
                </span>
              </div>
            ))}
          </div>
        )}
      </Space>
    </Modal>
  );
}

const codeStyle: CSSProperties = {
  background: 'var(--color-bg-elevated)',
  padding: '0 6px',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-primary)',
};

function resultRowStyle(
  borderColor: string,
  isFailure = false,
): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 'var(--radius-md)',
    border: `1px solid ${borderColor}`,
    background: isFailure
      ? 'var(--color-danger-bg-alt)'
      : 'var(--color-bg-card)',
  };
}
